import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import { useEffect } from 'react';

const Accessibility = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen">
      <SEO
        title="Accessibility Statement - EZ Cloud"
        description="EZ Cloud's commitment to digital accessibility. Learn about our WCAG 2.1 AA conformance goals and how to report accessibility issues."
        canonical="https://ezcloud.ai/accessibility"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Accessibility', url: 'https://ezcloud.ai/accessibility' },
        ]}
      />
      <Navigation />
      <Breadcrumbs />
      <div className="container mx-auto px-4 py-16 max-w-4xl">
        <h1 className="text-4xl font-bold text-white mb-8">Accessibility Statement</h1>
        <div className="max-w-none space-y-6 text-gray-300 text-lg leading-relaxed">
          <p className="text-sm text-gray-400">Last updated: April 2026</p>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Our Commitment</h2>
            <p>
              EZ Cloud is committed to ensuring digital accessibility for all users, including people with disabilities. We are continually improving the user experience for everyone and applying the relevant accessibility standards to ensure we provide equal access to all users.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Conformance Standard</h2>
            <p>
              EZ Cloud targets conformance with the Web Content Accessibility Guidelines (WCAG) 2.1 at Level AA. These guidelines explain how to make web content more accessible to people with a wide array of disabilities, including visual, auditory, physical, speech, cognitive, language, learning, and neurological disabilities.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Accessibility Features</h2>
            <p>EZ Cloud's platform includes the following accessibility features:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Semantic HTML structure for screen reader compatibility</li>
              <li>Keyboard navigation support throughout the application</li>
              <li>ARIA labels and roles on interactive elements</li>
              <li>Sufficient color contrast ratios for text and interactive elements</li>
              <li>Responsive design that supports browser zoom up to 200%</li>
              <li>Focus indicators on all interactive elements</li>
              <li>Form labels and error messages associated with input fields</li>
              <li>Alternative text for meaningful images</li>
            </ul>
            <p className="mt-4">
              Our platform is built using Material UI (MUI), a component library that provides built-in accessibility support including ARIA attributes, keyboard interactions, and focus management.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Ongoing Efforts</h2>
            <p>EZ Cloud is committed to the following ongoing accessibility efforts:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Automated accessibility testing integrated into our CI/CD pipeline</li>
              <li>Accessibility review as part of our code review and pull request process</li>
              <li>Regular accessibility audits of new features and updates</li>
              <li>Staff training on accessible development practices</li>
              <li>Monitoring and addressing accessibility issues reported by users</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Known Limitations</h2>
            <p>
              While we strive for WCAG 2.1 AA conformance, some content may not yet fully meet all criteria. We are actively working to identify and resolve any accessibility gaps. Areas of ongoing improvement include complex data tables, PDF invoice rendering, and chart visualizations.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Reporting Accessibility Issues</h2>
            <p>
              We welcome your feedback on the accessibility of EZ Cloud. If you encounter accessibility barriers or have suggestions for improvement, please contact us:
            </p>
            <p><strong className="text-white">Email:</strong> accessibility@ezcloud.ai</p>
            <p><strong className="text-white">Phone:</strong> +1 856-834-5080</p>
            <p><strong className="text-white">Address:</strong> 1134 S Black Horse Pike Suite 266, Blackwood, NJ 08012</p>
            <p className="mt-4">
              We aim to respond to accessibility feedback within 5 business days and to resolve reported issues as quickly as possible.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Compatibility</h2>
            <p>
              EZ Cloud is designed to be compatible with the following assistive technologies:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Screen readers (NVDA, JAWS, VoiceOver)</li>
              <li>Screen magnification software</li>
              <li>Speech recognition software</li>
              <li>Keyboard-only navigation</li>
            </ul>
            <p className="mt-4">
              EZ Cloud is designed to work with current versions of Chrome, Firefox, Safari, and Edge browsers.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Accessibility;
